import regionServiceAreaMapping from './region-servicearea-mapping.json'

// We only need this temporarily until the vehicle lists and hubs were migrated to the region concept
export const regionToServiceArea: Record<string, string[]> = {
  '9d28eb5f-3015-4e48-a129-2842461e7eb8': [
    'f89ee35c-10ff-4d3b-959e-07d2e7637119', // Hamburg
    '4bbfe046-7f37-44fe-bea8-6a2bc53b6241', // Hamburg CUGT
  ],
  '509f824e-cf6f-4e64-bed1-091593c2c78e': [
    'dd080b1e-9471-4798-8029-ae4ff2eee126', // Munich
  ],
  '43d127ba-d0e9-41f5-b233-9402934cd5cc': [
    '946cb18b-60e0-474e-8e96-1d93bffb9e82', // Austin
  ],
  '11a9a16a-18b9-4f9f-b02d-b6824a0d9285': [
    '61f780d5-04f5-4c7e-b051-92daeb6cc6ea', // Lake Nona
  ],
  '87f84afe-0c75-4419-8b1f-1af89ecd0d58': [
    '3321dbea-f00b-4998-a3e1-a6c4b5898387', // Hannover
  ],
}

type ServiceArea = {
  id: string
  uuid: string
}

type Region = {
  id: string
  name: string
  operator_tenant: string
  line_service_area_ids?: string[]
  service_areas: ServiceArea[]
}

type RegionServiceAreaMapping = {
  regions: Region[]
}

const regionServiceAreaMappingObject: RegionServiceAreaMapping =
  regionServiceAreaMapping

export const regionToServiceAreaData: Partial<Record<string, string[]>> =
  Object.fromEntries(
    regionServiceAreaMappingObject.regions.map(region => [
      region.id,
      region.service_areas.map(serviceArea => serviceArea.uuid),
    ]),
  )

export const regionToLineServiceAreaIds: Partial<Record<string, string[]>> =
  Object.fromEntries(
    regionServiceAreaMappingObject.regions
      .filter(region => region.line_service_area_ids !== undefined)
      .map(region => [region.id, region.line_service_area_ids ?? []]),
  )

export const serviceAreaToRegionData: Record<
  string,
  { id: string; name: string; operatorTenant: string }
> = Object.fromEntries(
  regionServiceAreaMappingObject.regions.flatMap(region =>
    region.service_areas.map(serviceArea => [
      serviceArea.uuid,
      {
        id: region.id,
        name: region.name,
        operatorTenant: region.operator_tenant,
      },
    ]),
  ),
)

/**
 * Maps region ID to the human-readable ID of its production service area (e.g.
 * "de-hamburg-01"), which is the first entry in the region's service area list —
 * the same convention `regionToIotName` relies on.
 */
export const regionToPrimaryServiceAreaId: Partial<Record<string, string>> =
  Object.fromEntries(
    regionServiceAreaMappingObject.regions.flatMap(region => {
      const primaryServiceArea = region.service_areas[0]
      return primaryServiceArea ? [[region.id, primaryServiceArea.id]] : []
    }),
  )

/**
 * Maps region ID to the IoT name component extracted from the first service area ID.
 * The IoT name is derived from the service area ID format (e.g., "de-hamburg-01" -> "hamburg").
 */
export const regionToIotName: Partial<Record<string, string>> =
  Object.fromEntries(
    regionServiceAreaMappingObject.regions.map(region => {
      const firstServiceArea = region.service_areas[0]
      const iotName = firstServiceArea?.id.split('-')[1] ?? ''
      return [region.id, iotName]
    }),
  )

/**
 * Maps a region's normalized name (lowercase, dash-separated — e.g. "hamburg",
 * "lake-nona") to its region ID.
 */
export const regionNameToId: Partial<Record<string, string>> =
  Object.fromEntries(
    regionServiceAreaMappingObject.regions.map(region => [
      region.name,
      region.id,
    ]),
  )
